<script setup>
    import { computed } from 'vue';

    const props = defineProps({
        abonos: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: null
        },
        comprando: {
            type: Boolean,
            default: false
        },
        eventName: {
            type: String,
            default: null
        }
    });

    const emit = defineEmits(['select']);

    const hayAbonos = computed(() => (props.abonos || []).length > 0);

    const estaPagado = abono => abono?.payment_status === 'PAID';

    const plazoVencido = abono => !!abono?.is_expired && !estaPagado(abono);

    const formatearPrecio = precio => {
        if (precio === null || precio === undefined) return '';

        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR'
        }).format(precio);
    };

    /**
     * Guardia contra el doble clic: crear carrito en SGA tarda, y sin esto un
     * segundo clic dispara otra peticion mientras la primera sigue en vuelo.
     */
    function seleccionar(abono) {
        if (!abono.is_payable || props.comprando) return;

        emit('select', abono);
    }
</script>

<template>
    <section class="seleccionar-abono-dia-abonado">
        <div class="tw:text-center tw:mb-5">
            <p class="tw:mb-0 tw:text-muted">
                {{ $t('front.sga.dia_abonado.descripcion') }}
            </p>
        </div>

        <!-- Cargando -->
        <div v-if="loading" class="tw:text-center tw:py-6">
            <b-spinner />
            <p class="tw:mt-2 tw:text-muted">{{ $t('front.sga.dia_abonado.cargando') }}</p>
        </div>

        <template v-else>
            <b-alert v-if="error" variant="danger" show class="tw:mb-4">
                {{ error }}
            </b-alert>

            <!-- Sin abonos con suplemento en este partido -->
            <div v-else-if="!hayAbonos" class="tw:text-center tw:py-8">
                <b-icon icon="calendar-x" class="tw:text-4xl tw:text-muted tw:mb-3" />
                <p class="tw:text-muted tw:mb-0">{{ $t('front.sga.dia_abonado.sin_abonos') }}</p>
            </div>

            <!-- Abonos: se muestran TODOS, también los ya pagados, para que el
                 abonado vea que su abono está al día. La columna se limita para
                 que la tarjeta no ocupe todo el ancho de la página. -->
            <div v-else class="lista-abonos-dia-abonado">
                <b-card
                    v-for="abono in abonos"
                    :key="abono.abonado_id"
                    data-test-abono-item
                    body-class="tw:py-3 tw:px-4"
                    class="tw:mb-2 tw:shadow-sm"
                    :class="{ 'tw:cursor-pointer': abono.is_payable && !comprando }"
                    @click="seleccionar(abono)"
                >
                    <div class="tw:flex tw:flex-col tw:md:flex-row tw:justify-between tw:items-start tw:md:items-center tw:gap-3">
                        <div>
                            <p class="tw:mb-1 tw:font-bold tw:text-base">
                                {{ abono.nombre || $t('front.sga.abono') }}
                                <span
                                    v-if="abono.n_abonado"
                                    class="tw:ml-2 tw:text-xs tw:font-mono tw:text-muted"
                                >
                                    #{{ abono.n_abonado }}
                                </span>
                            </p>
                            <p v-if="abono.asiento" class="tw:mb-0 tw:text-sm tw:text-muted">
                                <b-icon icon="geo-alt" class="tw:mr-1" />
                                {{ abono.asiento }}
                            </p>
                        </div>

                        <!-- Ya pagado -->
                        <div
                            v-if="estaPagado(abono)"
                            data-test-estado="pagado"
                            class="tw:flex tw:items-center tw:text-green-700 tw:font-semibold"
                        >
                            <b-icon icon="check-circle-fill" class="tw:mr-2" />
                            {{ $t('front.sga.dia_abonado.pagado') }}
                        </div>

                        <!-- Plazo vencido -->
                        <div
                            v-else-if="plazoVencido(abono)"
                            data-test-estado="caducado"
                            class="tw:flex tw:items-center tw:text-gray-600"
                        >
                            <b-icon icon="clock-history" class="tw:mr-2" />
                            {{ $t('front.sga.dia_abonado.caducado') }}
                        </div>

                        <!-- Pagable -->
                        <div v-else-if="abono.is_payable" class="tw:text-right">
                            <p class="tw:mb-2 tw:text-lg tw:font-bold">
                                {{ formatearPrecio(abono.precio) }}
                            </p>
                            <b-button
                                variant="primary"
                                :disabled="comprando"
                                @click.stop="seleccionar(abono)"
                            >
                                <span v-if="comprando">{{ $t('front.sga.dia_abonado.procesando') }}</span>
                                <span v-else>{{ $t('front.sga.dia_abonado.pagar') }}</span>
                            </b-button>
                        </div>
                    </div>
                </b-card>
            </div>
        </template>
    </section>
</template>

<style scoped>
.seleccionar-abono-dia-abonado {
    min-height: 200px;
}

/**
 * Mismo ancho que el stepper (1200px) para que la lista quede alineada con la
 * línea de pasos de arriba, igual que el resumen de la compra.
 */
.lista-abonos-dia-abonado {
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
}

.lista-abonos-dia-abonado :deep(.card) {
    margin-bottom: 8px;
}

.lista-abonos-dia-abonado :deep(.card-body) {
    padding-top: 12px;
    padding-bottom: 12px;
    padding-left: 16px;
    padding-right: 16px;
}
</style>
